import { z } from 'zod';
import { cents, configKey, entityId, isoDateTime } from './primitives.js';
import { listingStatus } from './listing.js';
import { subscriptionStatus } from './payments.js';

/**
 * CONTRATOS DO PAINEL ADMINISTRATIVO
 *
 * Projeções que só o back-office enxerga. Ficam separadas das do portal por um
 * motivo prático: elas carregam campos que o público nunca pode ver — nome do
 * dono, e-mail, motivo de reprovação, receita da assinatura.
 *
 * Manter os dois no mesmo schema é como o vazamento costuma acontecer: alguém
 * reaproveita o tipo "porque é quase igual" e um campo interno viaja para uma
 * resposta pública. Separado, o reaproveitamento indevido não compila.
 */

// ------------------------------------------------------------- anúncios

/**
 * Anúncio como o painel o lista.
 *
 * Não é o `listing` do portal: aqui entram dono, plano do dono e motivo da
 * última decisão de moderação — coisas que o visitante não vê.
 */
export const adminListing = z.object({
  id: entityId,
  code: z.string(),
  slug: z.string(),
  title: z.string(),
  status: listingStatus,
  priceCents: cents.nullable().default(null),
  totalAreaM2: z.number().nullable().default(null),
  city: z.string().nullable().default(null),
  state: z.string().nullable().default(null),

  /** Quem anunciou, e em que plano — decide limite, ranking e prazo. */
  advertiserId: entityId,
  advertiserName: z.string(),
  advertiserPlanSlug: configKey.nullable().default(null),

  featured: z.boolean().default(false),
  /** Dias ativos — insumo da regra dos 90 dias (Cap. 14). */
  activeDays: z.number().int().min(0).default(0),
  views: z.number().int().min(0).default(0),
  contacts: z.number().int().min(0).default(0),
  reports: z.number().int().min(0).default(0),

  publishedAt: isoDateTime.nullable().default(null),
  updatedAt: isoDateTime,
  expiresAt: isoDateTime.nullable().default(null),

  /** Preenchido quando a última decisão foi reprovar ou suspender. */
  moderationNote: z.string().nullable().default(null),
});

export type AdminListing = z.infer<typeof adminListing>;

/**
 * Ações de administração sobre um anúncio.
 *
 * `remove` é retirada de circulação, não exclusão: o registro continua para
 * auditoria e para o histórico do anunciante. Apagar de verdade é fluxo de
 * LGPD, separado e com outra permissão (Cap. 25).
 */
export const adminListingAction = z.enum([
  'approve',
  'reject',
  'suspend',
  'reactivate',
  'feature',
  'unfeature',
  'renew',
  'remove',
]);

export type AdminListingAction = z.infer<typeof adminListingAction>;

// --------------------------------------------------------- assinantes

/**
 * Anunciante e sua assinatura, na visão comercial.
 *
 * Junta o que hoje mora em dois lados — cadastro no portal, cobrança no
 * serviço de pagamentos — porque a pergunta que o painel responde é uma só:
 * "quem está pagando o quê, e desde quando?".
 */
export const subscriber = z.object({
  id: entityId,
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable().default(null),
  kind: z.enum(['owner', 'broker', 'agency', 'developer', 'company', 'organization', 'other']),
  document: z.string().nullable().default(null),
  verified: z.boolean().default(false),

  planSlug: configKey,
  planName: z.string(),
  subscriptionStatus: subscriptionStatus.nullable().default(null),
  /** Valor do ciclo corrente. Zero no gratuito. */
  amountCents: cents.default(0),
  billingPeriod: z.string().nullable().default(null),
  currentPeriodEnd: isoDateTime.nullable().default(null),
  /** Cortesia concedida pelo painel: assina sem cobrança, com prazo. */
  courtesyUntil: isoDateTime.nullable().default(null),

  /**
   * Há pagador conhecido — nome, e-mail e documento de alguma cobrança
   * anterior.
   *
   * Falso significa que a régua NÃO consegue faturar esta conta: o serviço de
   * pagamentos não tem cadastro de cliente, e o pagador só existe dentro de
   * uma cobrança. Um plano pago atribuído pelo painel a quem nunca pagou fica
   * ativo e nunca é cobrado — o painel avisa em vez de exibir o valor do plano
   * como se fosse receita.
   */
  hasPayerOnFile: z.boolean().default(false),

  activeListings: z.number().int().min(0).default(0),
  totalListings: z.number().int().min(0).default(0),
  /** Soma paga desde o cadastro — o número que responde "vale a pena reter?". */
  lifetimeValueCents: cents.default(0),

  memberSince: isoDateTime,
  lastAccessAt: isoDateTime.nullable().default(null),
  blocked: z.boolean().default(false),
});

export type Subscriber = z.infer<typeof subscriber>;

export const subscriberAction = z.enum(['change_plan', 'grant_courtesy', 'cancel', 'block', 'unblock']);
export type SubscriberAction = z.infer<typeof subscriberAction>;
